import React, { useState, useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Building2, ExternalLink, Package, Bookmark, Info, TrendingUp, CheckCircle2 } from "lucide-react"
import { toast } from "sonner"
import type { ProductSearchResult, ExternalProductSearchResult } from "@/lib/api/search.api"
import { useCompanyDetails } from "@/hooks/seller/useCompanyDetails"
import { cn } from "@/lib/utils"
import { Tooltip } from "@/components/ui/Tooltip"

export interface ProductSearchCardProps {
  product: ProductSearchResult | ExternalProductSearchResult
  companyName?: string
  isActiveTarget?: boolean
  onRequestQuote: () => void
  onPriceTrack?: () => void
  onPriceGraph?: () => void
  onBookmark?: (isBookmarked: boolean) => void
  showPriceRange?: boolean
}

export function PriceGraphIcon({ className = "size-5.5" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={cn("shrink-0", className)}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient
          id="priceGraphSphere"
          cx="38%"
          cy="32%"
          r="65%"
          fx="38%"
          fy="32%"
        >
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="45%" stopColor="#DCEBFE" />
          <stop offset="100%" stopColor="#BFDBFE" />
        </radialGradient>
      </defs>

      {/* Main sphere with soft radial gradient and border */}
      <circle
        cx="16"
        cy="16"
        r="14"
        fill="url(#priceGraphSphere)"
        stroke="#93C5FD"
        strokeWidth="1"
      />

      {/* Glossy highlight on top-left */}
      <path
        d="M 8 13 A 9 9 0 0 1 14 7"
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.9"
      />

      {/* Soft shadow curve on bottom-right */}
      <path
        d="M 15 26 A 10.5 10.5 0 0 0 26 15"
        stroke="#93C5FD"
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.7"
      />

      {/* Chart Line */}
      <path
        d="M 4.5 20.5 L 11.5 11.5 L 19 16.5 L 25 9.5"
        stroke="#2563EB"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Upward Arrow Head */}
      <polygon
        points="23,6.5 28.5,7.8 26.5,13.2 24.5,9.8"
        fill="#2563EB"
      />

      {/* Node 1: White filled node */}
      <circle
        cx="11.5"
        cy="11.5"
        r="2.1"
        fill="#FFFFFF"
        stroke="#2563EB"
        strokeWidth="1.6"
      />

      {/* Node 2: Amber/Gold filled node */}
      <circle
        cx="19"
        cy="16.5"
        r="2.1"
        fill="#F59E0B"
        stroke="#2563EB"
        strokeWidth="1.6"
      />
    </svg>
  )
}

export function ProductSearchCard({
  product,
  companyName,
  isActiveTarget,
  onRequestQuote,
  onPriceTrack,
  onPriceGraph,
  onBookmark,
  showPriceRange: propShowPriceRange,
}: ProductSearchCardProps) {
  const navigate = useNavigate()
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null)

  const isBrowseRoute =
    propShowPriceRange !== undefined
      ? propShowPriceRange
      : typeof window !== "undefined" && window.location.pathname.startsWith("/browse-products")

  const isExternal = 'productUrl' in product || 'externalSellerId' in product || 'companyUrl' in product || 'productImageUrl' in product
  const extProduct = isExternal ? (product as ExternalProductSearchResult) : null
  const intProduct = !isExternal ? (product as ProductSearchResult) : null

  const rawProduct = product as unknown as Record<string, any>

  const targetCompanyId = !isExternal
    ? (intProduct?.companyId || (typeof rawProduct.companyId === "string" ? rawProduct.companyId : undefined) || intProduct?.company?.id)
    : undefined

  const existingCompanyName =
    companyName ||
    intProduct?.companyName ||
    intProduct?.company?.name ||
    (typeof rawProduct.companyName === "string" ? rawProduct.companyName : undefined) ||
    (typeof rawProduct.company?.name === "string" ? rawProduct.company.name : undefined)

  const { data: companyData, isLoading: isLoadingCompany } = useCompanyDetails(
    targetCompanyId,
    !isExternal && !!targetCompanyId && !existingCompanyName
  )

  const resolvedCompanyName =
    existingCompanyName ||
    companyData?.name ||
    extProduct?.companyName

  const displayName = isExternal
    ? product.productName
    : (rawProduct.productName || intProduct?.name || product.productName)

  const handleCompanyClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!targetCompanyId) return

    const currentReturnUrl = typeof window !== "undefined"
      ? (window.location.pathname + window.location.search)
      : undefined

    const currentSearchParams = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null
    const currentCategory = currentSearchParams?.get("category") || undefined
    const currentSearchTerm = currentSearchParams?.get("searchTerm") || undefined

    navigate({
      to: "/explore/companies/$companyId",
      params: { companyId: targetCompanyId },
      search: {
        companyType: "internal",
        companyName: resolvedCompanyName || undefined,
        category: currentCategory,
        returnUrl: currentReturnUrl,
        searchTerm: currentSearchTerm,
      },
    })
  }

  const rawImages = Array.isArray(rawProduct.images) ? (rawProduct.images as { url?: string }[]) : undefined
  const rawImageUrls = Array.isArray(rawProduct.imageUrls) ? (rawProduct.imageUrls as { url?: string }[]) : undefined
  const rawImageUrl = typeof rawProduct.imageUrl === "string" ? rawProduct.imageUrl : null

  const displayImageUrl =
    extProduct?.productImageUrl ||
    intProduct?.imageUrl ||
    (intProduct?.imageUrls && intProduct.imageUrls.length > 0 ? intProduct.imageUrls[0].url : null) ||
    rawImageUrl ||
    rawImageUrls?.[0]?.url ||
    rawImages?.[0]?.url ||
    null

  const isImageFailed = !!displayImageUrl && failedImageUrl === displayImageUrl
  const showFallback = !displayImageUrl || isImageFailed

  const price = typeof intProduct?.price === "number" ? intProduct.price : (typeof rawProduct.price === "number" ? rawProduct.price : undefined)
  const currencyCode = intProduct?.currencyCode || (typeof rawProduct.currencyCode === "string" ? rawProduct.currencyCode : undefined)

  const formattedPrice = useMemo(() => {
    if (typeof rawProduct.price === "string" && rawProduct.price.trim() !== "") {
      return rawProduct.price.trim()
    }
    if (price === undefined || price === null) return null
    try {
      if (currencyCode) {
        return new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(price)
      }
    } catch {
      // fallback if currencyCode is not recognized
    }
    return `${currencyCode || "₹"} ${Number(price).toLocaleString("en-IN")}`
  }, [price, currencyCode, rawProduct])

  const rawPriceRange = (rawProduct.priceRange || (rawProduct as any).price_range || intProduct?.priceRange) as
    | { min?: number | null; max?: number | null; currencyCode?: string | null }
    | string
    | undefined
    | null

  const hasPriceRange =
    rawPriceRange != null &&
    (typeof rawPriceRange === "string"
      ? rawPriceRange.trim() !== ""
      : typeof rawPriceRange.min === "number" || typeof rawPriceRange.max === "number")

  const formattedPriceRange = useMemo(() => {
    if (!hasPriceRange || !rawPriceRange) return null
    if (typeof rawPriceRange === "string") return rawPriceRange.trim()
    const curr = rawPriceRange.currencyCode || currencyCode || "INR"
    const symbol = curr === "INR" ? "₹" : curr

    const formatNum = (num: number) =>
      Number(num).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      })

    const hasMin = typeof rawPriceRange.min === "number"
    const hasMax = typeof rawPriceRange.max === "number"

    if (hasMin && hasMax) {
      if (rawPriceRange.min === rawPriceRange.max) {
        return `${symbol} ${formatNum(rawPriceRange.min!)}`
      }
      return `${symbol} ${formatNum(rawPriceRange.min!)} - ${symbol} ${formatNum(rawPriceRange.max!)}`
    } else if (hasMin) {
      return `From ${symbol} ${formatNum(rawPriceRange.min!)}`
    } else if (hasMax) {
      return `Up to ${symbol} ${formatNum(rawPriceRange.max!)}`
    }
    return null
  }, [hasPriceRange, rawPriceRange, currencyCode])

  const moqVal = useMemo(() => {
    const val = rawProduct.moq || rawProduct.minimumOrderQuantity || rawProduct.minOrderQuantity || rawProduct.minOrder
    if (val != null && val !== "") {
      return val
    }
    return null
  }, [rawProduct])

  const skuText =
    rawProduct.sku ||
    rawProduct.productCode ||
    intProduct?.productCode ||
    (product.id ? product.id.slice(0, 8).toUpperCase() : undefined)

  const tagsList = useMemo(() => {
    const list: string[] = []
    if (intProduct?.productCategory) list.push(intProduct.productCategory)
    if (intProduct?.productSubCategory && intProduct.productSubCategory !== intProduct.productCategory) {
      list.push(intProduct.productSubCategory)
    }
    if (Array.isArray(rawProduct.tags)) {
      rawProduct.tags.forEach((t: string) => {
        if (typeof t === "string" && !list.includes(t)) list.push(t)
      })
    }
    return list
  }, [intProduct, rawProduct])

  const { visibleTags, remainingTagsCount } = useMemo(() => {
    if (!tagsList.length) return { visibleTags: [], remainingTagsCount: 0 }

    const firstTag = tagsList[0]
    // If first tag is longer than 13 characters, show only 1 pill
    const firstIsLarge = firstTag.length > 13

    if (firstIsLarge || tagsList.length === 1) {
      return {
        visibleTags: [firstTag],
        remainingTagsCount: tagsList.length - 1,
      }
    }

    // If first and second tag combined length exceeds 23 characters, show only 1 pill
    const secondTag = tagsList[1]
    if (secondTag && firstTag.length + secondTag.length > 23) {
      return {
        visibleTags: [firstTag],
        remainingTagsCount: tagsList.length - 1,
      }
    }

    // Otherwise show at most 2 pills
    const visible = tagsList.slice(0, 2)
    return {
      visibleTags: visible,
      remainingTagsCount: tagsList.length - visible.length,
    }
  }, [tagsList])

  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    const nextState = !isBookmarked
    setIsBookmarked(nextState)
    onBookmark?.(nextState)
    if (nextState) {
      toast.success("Product bookmarked", {
        description: displayName ? `${displayName} added to your bookmarks.` : undefined,
      })
    } else {
      toast.info("Bookmark removed", {
        description: displayName ? `${displayName} removed from bookmarks.` : undefined,
      })
    }
  }

  return (
    <div
      onClick={onRequestQuote}
      className={cn(
        "group relative isolate overflow-hidden flex flex-col justify-between h-full bg-white rounded-2xl p-4 cursor-pointer text-left font-sans border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200",
        isActiveTarget ? "border-2 border-primary shadow-sm" : ""
      )}
    >
      <div className="space-y-3">
        {/* Top Header: Square Thumbnail + Title & SKU/Stock + Bookmark */}
        <div className="flex items-start gap-3 justify-between">
          {/* Square Thumbnail Image */}
          <div className="size-13 rounded-xl bg-slate-50 border border-slate-200/70 flex items-center justify-center shrink-0 overflow-hidden relative shadow-2xs group-hover:scale-105 transition-transform duration-300">
            {showFallback ? (
              <div className="size-full flex items-center justify-center text-slate-300">
                <Package className="size-6 text-slate-300 shrink-0" />
              </div>
            ) : (
              <img
                src={displayImageUrl}
                alt={displayName}
                className="size-full object-cover"
                onError={() => setFailedImageUrl(displayImageUrl)}
              />
            )}
          </div>

          {/* Title & SKU / Stock */}
          <div className="min-w-0 flex-1 space-y-1">
            <h3
              className="text-[14px] font-bold text-slate-900 tracking-tight leading-snug truncate group-hover:text-primary transition-colors"
              title={displayName}
            >
              {displayName}
            </h3>

            <div className="flex items-center gap-2 flex-wrap">
              {skuText && (
                <span className="text-[11.5px] font-medium text-slate-500 truncate">
                  SKU {skuText}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 shrink-0">
                <CheckCircle2 className="size-3 text-emerald-600 shrink-0" />
                <span>In-Stock</span>
              </span>
            </div>
          </div>

          {/* Bookmark Button (Visible on card hover or when bookmarked) */}
          <button
            type="button"
            onClick={handleBookmarkToggle}
            className={cn(
              "size-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-all duration-200 shrink-0 cursor-pointer",
              isBookmarked
                ? "opacity-100 text-primary"
                : "opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700"
            )}
            title={isBookmarked ? "Remove bookmark" : "Bookmark product"}
          >
            <Bookmark className={cn("size-4", isBookmarked ? "fill-primary text-primary" : "")} />
          </button>
        </div>

        {/* Tags Row: Untrimmed Category Pills + Price Graph + Tooltip on +Count */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {visibleTags.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 whitespace-nowrap"
            >
              {tag}
            </span>
          ))}

          {remainingTagsCount > 0 && (
            <Tooltip
              tooltipStyle={{ maxWidth: "260px", whiteSpace: "normal" }}
              content={
                <div
                  className="p-1 text-left space-y-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                    Associated Tags ({tagsList.length})
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {tagsList.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-muted text-foreground border border-border/80"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              }
              position="bottom"
            >
              <span className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600 cursor-pointer transition-colors block">
                +{remainingTagsCount}
              </span>
            </Tooltip>
          )}

          {/* Price Graph Button in Tags Row */}
          {isBrowseRoute && hasPriceRange && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onPriceGraph ? onPriceGraph() : onRequestQuote()
              }}
              className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-700 hover:text-primary transition-colors cursor-pointer shrink-0 ml-auto group/graph"
              title="View price graph"
            >
              <PriceGraphIcon className="size-4" />
              <span className="group-hover/graph:underline">Price graph</span>
            </button>
          )}
        </div>

        {/* Middle Stats: Price Range on browse-products, Price & MOQ on search */}
        <div className="flex items-start justify-between gap-4 pt-1 min-h-[46px]">
          {isBrowseRoute ? (
            /* Browse Products Mode: Render Price Range if present; if missing, keep space and don't render field */
            <div className="text-left min-w-0 flex-1">
              {formattedPriceRange ? (
                <>
                  <span className="block text-[11px] font-medium text-slate-500 mb-0.5">
                    Price Range
                  </span>
                  <span className="text-[17px] font-extrabold text-slate-900 tracking-tight block truncate">
                    {formattedPriceRange}
                  </span>
                </>
              ) : (
                /* Preserves layout space when Price Range is missing */
                <div className="h-[42px]" aria-hidden="true" />
              )}
            </div>
          ) : (
            /* Search / FindSupplierPage Mode: Render Price & MOQ if present, otherwise preserve space */
            <>
              {/* Price */}
              <div className="text-left min-w-0 flex-1">
                {formattedPrice ? (
                  <>
                    <span className="block text-[11px] font-medium text-slate-500 mb-0.5">
                      Price
                    </span>
                    <span className="text-[17px] font-extrabold text-slate-900 tracking-tight block truncate">
                      {formattedPrice}
                    </span>
                  </>
                ) : (
                  /* Preserves layout space when Price is missing */
                  <div className="h-[42px]" aria-hidden="true" />
                )}
              </div>

              {/* Minimum Order */}
              <div className="text-right shrink-0">
                {moqVal ? (
                  <>
                    <span className="block text-[11px] font-medium text-slate-500 mb-0.5">
                      Minimum Order
                    </span>
                    <span className="text-[13.5px] font-bold text-slate-800 tracking-tight block">
                      {typeof moqVal === "number" || !isNaN(Number(moqVal))
                        ? `${moqVal} units`
                        : moqVal}
                    </span>
                  </>
                ) : (
                  /* Preserves layout space when MOQ is missing */
                  <div className="h-[42px]" aria-hidden="true" />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer Row: Company / Variants on Left & Request Quote on Right */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100/90 mt-3">
        {/* Left: Company link or Variant count */}
        <div className="min-w-0 flex-1 pr-2">
          {(resolvedCompanyName || isLoadingCompany) ? (
            <div
              onClick={!isExternal && targetCompanyId ? handleCompanyClick : undefined}
              className={cn(
                "flex items-center gap-1.5 text-[11.5px] text-slate-500 font-medium truncate transition-colors",
                !isExternal && targetCompanyId && "hover:text-primary cursor-pointer"
              )}
              title={resolvedCompanyName || undefined}
            >
              <Building2 className="size-3.5 text-slate-400 shrink-0" />
              <span className="truncate">{resolvedCompanyName}</span>
              {extProduct?.companyUrl && (
                <a
                  href={extProduct.companyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary hover:underline flex items-center gap-0.5 shrink-0 ml-0.5"
                  title="Visit Company Page"
                >
                  <ExternalLink className="size-2.5" />
                </a>
              )}
            </div>
          ) : rawProduct.variantCount ? (
            <span className="text-[12px] font-medium text-slate-600">
              Variants ({rawProduct.variantCount})
            </span>
          ) : (
            <span className="text-[11.5px] font-medium text-slate-400">
              Variants (1)
            </span>
          )}
        </div>

        {/* Right: Request Quote button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRequestQuote()
          }}
          className="h-8.5 px-4 bg-primary hover:bg-primary-hover text-primary-foreground rounded-xl flex items-center justify-center text-[12px] font-bold shadow-sm shadow-primary/20 hover:shadow hover:shadow-primary/30 active:scale-[0.98] transition-all duration-200 cursor-pointer shrink-0"
        >
          Request Quote
        </button>
      </div>
    </div>
  )
}
